import { NotificationSeverity } from '@/components/notifications/Notification';
import { CompanySchema } from '../models/Company';
import { useNotification } from '@/hooks';
import { useCallback } from 'react';
import {
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useToggleCompanyMutation,
} from '../services/companies.service';

const useCompanyActions = () => {
  const [createCompany] = useCreateCompanyMutation();
  const [updateCompany] = useUpdateCompanyMutation();
  const [deleteCompany, { isLoading: isDeleting }] = useDeleteCompanyMutation();
  const [toggleCompany] = useToggleCompanyMutation();

  const { showNotification } = useNotification();

  const onCreate = useCallback(
    async (data: CompanySchema, reset?: () => void) => {
      try {
        await createCompany(data).unwrap();
        showNotification('Compañía creada correctamente', NotificationSeverity.success);
        reset?.();
      } catch (e) {
        showNotification('Error al crear la compañía', NotificationSeverity.error);
      }
    },
    [createCompany, showNotification]
  );

  const onUpdate = useCallback(
    async (id: string, data: Partial<CompanySchema>, navigateBack?: () => void) => {
      try {
        await updateCompany({ id, data }).unwrap();
        showNotification('Compañía actualizada correctamente', NotificationSeverity.success);
        navigateBack?.();
      } catch (e) {
        showNotification('Error al actualizar la compañía', NotificationSeverity.error);
      }
    },
    [updateCompany, showNotification]
  );

  const onDelete = useCallback(
    async (id: string) => {
      try {
        await deleteCompany(id).unwrap();
        showNotification('Compañía eliminada correctamente', NotificationSeverity.success);
      } catch (e) {
        showNotification('Error al eliminar la compañía', NotificationSeverity.error);
      }
    },
    [deleteCompany, showNotification]
  );

  const onToggle = useCallback(
    async (id: string) => {
      try {
        await toggleCompany(id).unwrap();
        showNotification('Estado de compañía actualizado', NotificationSeverity.success);
      } catch (e) {
        showNotification('Error al cambiar estado de la compañía', NotificationSeverity.error);
      }
    },
    [toggleCompany, showNotification]
  );

  return { onCreate, onUpdate, onDelete, onToggle, isDeleting };
};

export default useCompanyActions;